import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Card,
  Heading,
  Alert,
  Icon,
  Spinner,
  Button,
} from '@chakra-ui/react';
import { FiCheckCircle, FiAlertTriangle, FiFileText, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { evidenceAPI } from '../../services/api';
import type { SystemStandard } from '../../types';
import { useTranslation } from 'react-i18next';
import { getAssessmentUnitLabels } from '../../utils/frameworkTerminology';

export interface RequiredEvidenceItem {
  type_key: string;
  label: string;
  label_ar: string;
  label_en: string;
  mandatory: boolean;
  guidance: string;
}

export interface RequiredMeasure {
  measure_id: string;
  measure_title: string;
  coverage_section: string;
  required_evidence: RequiredEvidenceItem[];
  mandatory_count: number;
}

export interface RequiredGroup {
  section_id: string;
  section_title: string;
  measures: RequiredMeasure[];
}

export interface ChecklistResponse {
  language: string;
  groups: RequiredGroup[];
  summary: {
    section_count: number;
    measure_count: number;
    distinct_evidence_types: number;
    mandatory_evidence_types: number;
  };
}

interface RequiredEvidenceChecklistProps {
  selectedStandard: SystemStandard | null;
  selectedSections: string[];
  selectedSubsections?: { [key: string]: string[] };
  linkedEvidenceBySection?: {
    [sectionId: string]: Array<{
      id: string;
      filename: string;
      evidence_types?: string[];
      auto_detected_sections?: Record<string, { matched_types?: string[] }>;
    }>;
  };
  manualEvidenceBySectionType?: {
    [sectionId: string]: {
      [typeKey: string]: Array<{
        id: string;
        filename: string;
        evidence_types?: string[];
        auto_detected_sections?: Record<string, { matched_types?: string[] }>;
      }>;
    };
  };
  onDataUpdate?: (data: ChecklistResponse | null) => void;
}

const RequiredEvidenceChecklist: React.FC<RequiredEvidenceChecklistProps> = ({
  selectedStandard,
  selectedSections,
  selectedSubsections,
  linkedEvidenceBySection = {},
  manualEvidenceBySectionType = {},
  onDataUpdate,
}) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ChecklistResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const assessmentTerminology = useMemo(
    () => getAssessmentUnitLabels(selectedStandard?.standard_type, selectedSections, i18n.language),
    [selectedStandard?.standard_type, selectedSections, i18n.language]
  );

  useEffect(() => {
    if (!selectedStandard || selectedSections.length === 0) {
      setData(null);
      onDataUpdate?.(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await evidenceAPI.getRequiredEvidence({
          system_standard_id: Number(selectedStandard.id),
          selected_sections: selectedSections,
          selected_subsections: selectedSubsections,
        });
        if (!cancelled) {
          setData(res);
          onDataUpdate?.(res);
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message || t('createReview.requiredEvidenceLoadFailed'));
          onDataUpdate?.(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onDataUpdate, selectedStandard?.id, JSON.stringify(selectedSections), JSON.stringify(selectedSubsections), t]);

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const matchedEvidenceForType = useMemo(
    () =>
      (coverageSection: string, ev: RequiredEvidenceItem) => {
        const explicitManualMatches = manualEvidenceBySectionType[coverageSection]?.[ev.type_key] || [];
        const linkedForSection = linkedEvidenceBySection[coverageSection] || [];

        if (ev.type_key === 'supporting_document') {
          return explicitManualMatches.length > 0
            ? Array.from(new Map([...explicitManualMatches, ...linkedForSection].map((e) => [String(e.id), e])).values())
            : linkedForSection;
        }

        // Pool ALL evidence linked to any measure, not just this coverage_section.
        // This allows a file linked to measure 1-1-3 to satisfy the same evidence
        // type requirement on measures 2-1-3 and 3-1-3 — the consultant only needs
        // to upload one approval record, not one per measure.
        const allLinked = Array.from(
          new Map(
            Object.values(linkedEvidenceBySection)
              .flat()
              .map((e) => [String(e.id), e])
          ).values()
        );

        const typeMatches = allLinked.filter((evidence) => {
          // 1. Section-specific match from semantic auto-detect (most specific)
          const sectionTypes = (evidence.auto_detected_sections?.[coverageSection]?.matched_types || []).filter(Boolean);
          if (sectionTypes.length > 0) return sectionTypes.includes(ev.type_key);
          // 2. Global evidence-type classification (covers cross-section reuse)
          return (evidence.evidence_types || []).includes(ev.type_key);
        });

        return Array.from(
          new Map([...explicitManualMatches, ...typeMatches].map((e) => [String(e.id), e])).values()
        );
      },
    [linkedEvidenceBySection, manualEvidenceBySectionType]
  );

  // Whether a specific required-evidence type for a measure is satisfied by the
  // documents linked to that exact measure/subsection coverage key.
  const isTypeSatisfied = useMemo(
    () => (coverageSection: string, ev: RequiredEvidenceItem): boolean =>
      matchedEvidenceForType(coverageSection, ev).length > 0,
    [matchedEvidenceForType]
  );

  // Aggregate satisfaction stats for a group (across its measures / required types).
  const groupStats = useMemo(
    () =>
      (group: RequiredGroup) => {
        let total = 0;
        let satisfied = 0;
        let mandatoryTotal = 0;
        let mandatorySatisfied = 0;
        for (const m of group.measures) {
          for (const ev of m.required_evidence) {
            const ok = isTypeSatisfied(m.coverage_section, ev);
            total += 1;
            if (ok) satisfied += 1;
            if (ev.mandatory) {
              mandatoryTotal += 1;
              if (ok) mandatorySatisfied += 1;
            }
          }
        }
        return { total, satisfied, mandatoryTotal, mandatorySatisfied };
      },
    [isTypeSatisfied]
  );

  const missingSections = useMemo(() => {
    if (!data) return [];
    return data.groups.filter((g) => {
      const s = groupStats(g);
      return s.satisfied < s.total;
    });
  }, [data, groupStats]);

  if (!selectedStandard || selectedSections.length === 0) return null;

  return (
    <Card.Root variant="outline" borderColor="brand.200">
      <Card.Header>
        <HStack justify="space-between" flexWrap="wrap">
          <VStack align="start" gap={0}>
            <Heading size="sm">{t('createReview.requiredEvidenceTitle')}</Heading>
            <Text fontSize="xs" color="gray.600">
              {t('createReview.requiredEvidenceSubtitle', {
                unitPlural: assessmentTerminology.plural,
              })}
            </Text>
          </VStack>
          {data && (
            <Badge colorPalette="brand" variant="subtle">
              {t('createReview.requiredEvidenceMeasureCount', {
                count: data.summary.measure_count,
                unit: data.summary.measure_count === 1 ? assessmentTerminology.singular : assessmentTerminology.plural,
              })}
            </Badge>
          )}
        </HStack>
      </Card.Header>
      <Card.Body>
        {loading && (
          <HStack gap={2} color="gray.600">
            <Spinner size="sm" />
            <Text fontSize="sm">
              {t('createReview.requiredEvidenceLoading', {
                unitPlural: assessmentTerminology.plural,
              })}
            </Text>
          </HStack>
        )}

        {error && (
          <Alert.Root status="error" borderRadius="md">
            <Alert.Indicator />
            <Alert.Description>{error}</Alert.Description>
          </Alert.Root>
        )}

        {data && !loading && (
          <VStack gap={4} align="stretch">
            {/* Missing evidence summary */}
            {missingSections.length > 0 ? (
              <Alert.Root status="warning" borderRadius="md">
                <Alert.Indicator>
                  <Icon as={FiAlertTriangle} />
                </Alert.Indicator>
                <Box>
                  <Alert.Title>
                    {t('createReview.requiredEvidenceMissingTitle', {
                      count: missingSections.length,
                      unitLabel:
                        missingSections.length === 1
                          ? assessmentTerminology.singular
                          : assessmentTerminology.plural,
                    })}
                  </Alert.Title>
                  <Alert.Description>
                    {t('createReview.requiredEvidenceMissingDescription', {
                      unitSingular: assessmentTerminology.singular,
                      unitPlural: assessmentTerminology.plural,
                    })}
                  </Alert.Description>
                </Box>
              </Alert.Root>
            ) : (
              <Alert.Root status="success" borderRadius="md">
                <Alert.Indicator>
                  <Icon as={FiCheckCircle} />
                </Alert.Indicator>
                <Alert.Description>
                  {t('createReview.requiredEvidenceAllCovered', {
                    unitPlural: assessmentTerminology.plural,
                  })}
                </Alert.Description>
              </Alert.Root>
            )}

            {/* Per-section groups */}
            {data.groups.map((group) => {
              const stats = groupStats(group);
              const fullyCovered = stats.total > 0 && stats.satisfied === stats.total;
              const partiallyCovered = !fullyCovered && stats.satisfied > 0;
              const coverageLabel = fullyCovered
                ? t('createReview.requiredEvidenceCovered')
                : partiallyCovered
                  ? t('createReview.requiredEvidencePartial')
                  : t('createReview.requiredEvidenceNoEvidence');
              const coveragePalette = fullyCovered ? 'green' : partiallyCovered ? 'yellow' : 'orange';
              const isOpen = expanded.has(group.section_id);
              return (
                <Card.Root key={group.section_id} variant="outline" size="sm">
                  <Card.Body>
                    <HStack
                      justify="space-between"
                      cursor="pointer"
                      onClick={() => toggle(group.section_id)}
                    >
                      <HStack gap={2}>
                        <Icon as={isOpen ? FiChevronDown : FiChevronRight} color="gray.500" />
                        <Text fontSize="sm" fontWeight="semibold">
                          {group.section_title}
                        </Text>
                        <Badge variant="subtle" colorPalette="gray" fontSize="xs">
                          {t('createReview.requiredEvidenceMeasureCount', {
                            count: group.measures.length,
                            unit: group.measures.length === 1 ? assessmentTerminology.singular : assessmentTerminology.plural,
                          })}
                        </Badge>
                      </HStack>
                      <HStack gap={2}>
                        <Text fontSize="10px" color="gray.500">
                          {t('createReview.requiredEvidenceProgress', {
                            satisfied: stats.satisfied,
                            total: stats.total,
                          })}
                        </Text>
                        <Badge
                          colorPalette={coveragePalette}
                          variant="subtle"
                          fontSize="xs"
                        >
                          {coverageLabel}
                        </Badge>
                      </HStack>
                    </HStack>

                    {isOpen && (
                      <VStack gap={3} align="stretch" mt={3} pl={6}>
                        {group.measures.map((m) => (
                          <Box key={m.measure_id} borderStartWidth="2px" borderColor="gray.200" pl={3}>
                            <HStack gap={2} mb={1} align="start">
                              <Badge variant="outline" colorPalette="brand" fontSize="10px">
                                {m.measure_id}
                              </Badge>
                              <Text fontSize="xs" color="gray.700" lineClamp={2}>
                                {m.measure_title}
                              </Text>
                            </HStack>
                            <VStack gap={2} align="stretch" pt={1}>
                              {m.required_evidence.map((ev) => {
                                const matchedEvidence = matchedEvidenceForType(m.coverage_section, ev);
                                const satisfied = isTypeSatisfied(m.coverage_section, ev);
                                return (
                                  <Box
                                    key={ev.type_key}
                                    borderWidth="1px"
                                    borderColor={satisfied ? 'green.200' : ev.mandatory ? 'red.200' : 'blue.200'}
                                    bg={satisfied ? 'green.50' : ev.mandatory ? 'red.50' : 'blue.50'}
                                    borderRadius="md"
                                    p={2}
                                  >
                                    <HStack justify="space-between" align="start" gap={2}>
                                      <HStack gap={2} align="start">
                                        <Icon
                                          as={satisfied ? FiCheckCircle : FiFileText}
                                          mt="2px"
                                          boxSize="12px"
                                          color={satisfied ? 'green.600' : ev.mandatory ? 'red.600' : 'blue.600'}
                                        />
                                        <VStack align="start" gap={1}>
                                          <Text fontSize="xs" fontWeight="medium" color="gray.800">
                                            {ev.label}
                                            {ev.mandatory ? ' *' : ''}
                                          </Text>
                                          <Text fontSize="10px" color="gray.600">
                                            {ev.guidance}
                                          </Text>
                                        </VStack>
                                      </HStack>
                                      <Badge
                                        colorPalette={satisfied ? 'green' : ev.mandatory ? 'red' : 'blue'}
                                        variant={satisfied || ev.mandatory ? 'solid' : 'subtle'}
                                        fontSize="10px"
                                      >
                                        {satisfied
                                          ? t('createReview.requiredEvidenceMapped')
                                          : t('createReview.requiredEvidenceRemaining')}
                                      </Badge>
                                    </HStack>

                                    {matchedEvidence.length > 0 ? (
                                      <VStack align="start" gap={1} mt={2} pl={5}>
                                        <Text fontSize="10px" color="gray.600">
                                          {t('createReview.requiredEvidenceMappedFilesLabel')}
                                        </Text>
                                        <HStack gap={1} flexWrap="wrap">
                                          {matchedEvidence.map((evidence) => (
                                            <Badge key={`${ev.type_key}-${evidence.id}`} colorPalette="green" variant="subtle" fontSize="10px">
                                              {evidence.filename}
                                            </Badge>
                                          ))}
                                        </HStack>
                                      </VStack>
                                    ) : (
                                      <Text fontSize="10px" color="gray.600" mt={2} pl={5}>
                                        {t('createReview.requiredEvidenceNoFilesMapped')}
                                      </Text>
                                    )}
                                  </Box>
                                );
                              })}
                            </VStack>
                          </Box>
                        ))}
                      </VStack>
                    )}
                  </Card.Body>
                </Card.Root>
              );
            })}

            <HStack gap={4} fontSize="xs" color="gray.500" flexWrap="wrap">
              <HStack gap={1}>
                <Badge colorPalette="green" variant="solid" fontSize="10px">
                  <Icon as={FiCheckCircle} boxSize="10px" />
                </Badge>
                <Text>{t('createReview.requiredEvidenceProvidedLegend')}</Text>
              </HStack>
              <HStack gap={1}>
                <Badge colorPalette="red" variant="solid" fontSize="10px">*</Badge>
                <Text>{t('createReview.requiredEvidenceMandatoryLegend')}</Text>
              </HStack>
              <HStack gap={1}>
                <Badge colorPalette="blue" variant="subtle" fontSize="10px">i</Badge>
                <Text>{t('createReview.requiredEvidenceOptionalLegend')}</Text>
              </HStack>
            </HStack>

            {data.groups.length === 0 && (
              <Text fontSize="sm" color="gray.500">
                {t('createReview.requiredEvidenceNone')}
              </Text>
            )}

            {/* Collapse/expand all convenience */}
            <Button
              size="xs"
              variant="ghost"
              alignSelf="start"
              onClick={() =>
                setExpanded((prev) =>
                  prev.size === data.groups.length
                    ? new Set()
                    : new Set(data.groups.map((g) => g.section_id))
                )
              }
            >
              {expanded.size === data.groups.length
                ? t('createReview.requiredEvidenceCollapseAll')
                : t('createReview.requiredEvidenceExpandAll')}
            </Button>
          </VStack>
        )}
      </Card.Body>
    </Card.Root>
  );
};

export default RequiredEvidenceChecklist;
